import { useEffect, useRef, useState } from "react";
import EditableInfoCard from "./EditableInfoCard";
import { useGlobalStates } from "../../utlis/ClubsContext";
import {
  validateRequired,
  validateNumeric,
  validatePercentage,
  ErrorMessage,
  formatCurrency,
} from "../../utlis/function.js";
import { callApi } from "../../services/ApiServices.js";
import TimeDurationModal from "../common/TimeDurationModal.jsx";
import toast from "react-hot-toast";
import { UserRole } from "../../utlis/constant.js";
import CommonLoader from "../common/CommonLoader.jsx";

const PROMOTIONAL_SECTIONS = [
  {
    type: "weather",
    title: "Weather",
    description: "Set pricing for weather promotional ad spots across multiple plans.",
  },
  {
    type: "sports",
    title: "Sports",
    description: "Set pricing for sports promotional ad spots across multiple plans.",
  },
  {
    type: "finance",
    title: "Finance",
    description: "Set pricing for finance promotional ad spots across multiple plans.",
  },
];

const PROMO_TYPES = PROMOTIONAL_SECTIONS.map(({ type }) => type);

// A promotional pricing row is identified by its `type` column: "weather" | "sports" | "finance"
const isPromoPricing = (pricing) => PROMO_TYPES.includes(pricing?.type);

const findPromoPricing = (pricings = [], type) =>
  pricings.find((p) => p.type === type);

const EMPTY_PROMO = { status: false, price1Mon: "", price6Mon: "", price12Mon: "", spots: "" };

// Only returns types the club detail actually has rows for, so a refetch never resets local state
const buildPromoState = (club) =>
  PROMO_TYPES.reduce((acc, type) => {
    const pricing = findPromoPricing(club?.pricings, type);
    if (!pricing) return acc;
    acc[type] = {
      status: Boolean(pricing.status),
      price1Mon: pricing.price1Mon ?? "",
      price6Mon: pricing.price6Mon ?? "",
      price12Mon: pricing.price12Mon ?? "",
      spots: pricing.spots ?? "",
    };
    return acc;
  }, {});

const PricingTab = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [openTimeDutaionModal, setOpenTimeDutaionModal] = useState(false);
  const [open30SecModal, setOpen30SecModal] = useState(false);
  const [savingField, setSavingField] = useState(null);
  const geoToggleLock = useRef(false);
  const [geoTargeting, setGeoTargeting] = useState({
    geoTargetStatus: false,
    setupFee: "",
    monthlyFee: "",
  });
  const[isCpmVisible, setIsCpmVisible] = useState(false);

  const [designService, setDesignService] = useState({
    DesignServiceStatus: false,
    designFee: "",
  });

  const [promoPricing, setPromoPricing] = useState(() =>
    Object.fromEntries(PROMO_TYPES.map((type) => [type, EMPTY_PROMO]))
  );

  const [clubHandleDesign, setClubHandleDesign] = useState(false);
  const [drsDesignSupport, setDrsDesignSupport] = useState(false);

  const [dayparts, setDayparts] = useState([
    {
      label: "Open 10:00am",
      value: "20%",
      discount: "50%",
      adsLimit: "10",
      type: "open",
    },
    {
      label: "10:00am - 04:00pm",
      value: "10:00AM - 04:00PM",
      discount: "30%",
      adsLimit: "10",
      type: "time",
    },
    {
      label: "04:00pm",
      value: "04:00PM",
      discount: "50%",
      adsLimit: "10",
      type: "close",
    },
    {
      label: "Weekends Only",
      value: "Weekends Only",
      discount: "50%",
      adsLimit: "10",
      type: "weekend",
    },
  ]);

  const { selectedClub, handleSelectedClubDetail ,userRole} = useGlobalStates();

  console.log("selected Club", selectedClub);

  const formatPricingData = (pricings = []) => {
    const result = {};

    pricings.forEach((item) => {
      const key = `${item.duration}sec`;

      result[key] = {
        "1month": item.price1Mon,
        "6months": item.price6Mon,
        "12months": item.price12Mon,
      };
    });

    return result;
  };

  useEffect(() => {
    if (!selectedClub) return;

    //  Pricing
    if (selectedClub.pricings?.length) {
      const formattedPricing = formatPricingData(selectedClub.pricings);
    }

    //  Dayparts
    if (selectedClub.dayparts) {
      setDayparts(selectedClub.dayparts);
    }

    //  Geo targeting
    if (selectedClub.geoTargetStatus) {
      setGeoTargeting((prev) => ({
        ...prev,
        geoTargetStatus: selectedClub.geoTargetStatus,
        setupFee: selectedClub.geoTargetFee,
        monthlyFee: selectedClub.geoTargetRecFee,
      }));
    }

    //  Design service
    if (selectedClub.designServiceStatus) {
      setDesignService((prev) => ({
        ...prev,
        DesignServiceStatus: selectedClub?.designServiceStatus,
        designFee: selectedClub.designServiceFee,
      }));
    }

    //  Club handle design
    if (selectedClub?.clubHandleDesign) {
      setClubHandleDesign(selectedClub?.clubHandleDesign);
    }

    if (selectedClub?.drsDesignSupport) {
      setDrsDesignSupport(selectedClub?.drsDesignSupport);
    }
    if(selectedClub?.isCpmHide){
      setIsCpmVisible(selectedClub?.isCpmHide);
    }

    //  Weather / Sports / Finance
    setPromoPricing((prev) => ({ ...prev, ...buildPromoState(selectedClub) }));
  }, [selectedClub]);

  const handleGeoTargetingEdit = async (key, newValue) => {
    try {
      setSavingField(key);
      const payload = {};
      if (key === "setupFee") {
        payload.geoTargetFee = Number(newValue.replace(/[^0-9.]/g, ""));
      }
      if (key === "monthlyFee") {
        payload.geoTargetRecFee = Number(newValue.replace(/[^0-9.]/g, ""));
      }

      const res = await callApi({
        method: "put",
        url: `club/update/${selectedClub.id}`,
        data: {
          fields: payload,
        },
      });
      if (res.status === 200) {
        setGeoTargeting({ ...geoTargeting, [key]: newValue });
        handleSelectedClubDetail(selectedClub);
      }
    } catch (error) {
      console.log("geo targeting", error);
    } finally {
      setSavingField(null);
    }
  };

  const handleGeoToggle = async () => {
    if (geoToggleLock.current) return;
    geoToggleLock.current = true;
    const newStatus = !geoTargeting.geoTargetStatus;

    try {
      setSavingField("geoToggle");

      const payload = {
        geoTargetStatus: newStatus,
      };

      //  If turning ON, optionally send existing fees
      if (newStatus) {
        if (geoTargeting.setupFee) {
          payload.geoTargetFee = Number(
            geoTargeting.setupFee.replace(/[^0-9.]/g, "")
          );
        }

        if (geoTargeting.monthlyFee) {
          payload.geoTargetRecFee = Number(
            geoTargeting.monthlyFee.replace(/[^0-9.]/g, "")
          );
        }
      }

      const res = await callApi({
        method: "put",
        url: `club/update/${selectedClub.id}`,
        data: { fields: payload },
      });

      if (res.status === 200) {
        setGeoTargeting((prev) => ({
          ...prev,
          geoTargetStatus: newStatus,
        }));
        handleSelectedClubDetail(selectedClub);
      }
    } catch (error) {
      console.error("Geo toggle error", error);
    } finally {
      setSavingField(null);
      geoToggleLock.current = false;
    }
  };

    const HideCpmOption = async () => {

    const newStatus = !isCpmVisible;

    try {
      setSavingField("isCpmHide");

      const payload = {
        isCpmHide: newStatus,
      };
      const res = await callApi({
        method: "put",
        url: `club/update/${selectedClub.id}`,
        data: { fields: payload },
      });
      if (res.status === 200) {
        toast.success(
          isCpmVisible
            ? "CPM is now visible on the website"
            : "CPM is now hidden from the website",
          { id: "handleDesignToast" },
        );
        setIsCpmVisible(newStatus);
        handleSelectedClubDetail(selectedClub);
      }
    } catch (error) {
      console.error("Hide Cpm error", error);
    } finally {
      setSavingField(null);
    }
  };

  const handleDesignServiceEdit = async (key, newValue) => {
    try {
      const res = await callApi({
        method: "put",
        url: `club/update/${selectedClub.id}`,
        data: {
          fields: {
            designServiceFee: Number(newValue.replace(/[^0-9.]/g, "")),
          },
        },
      });
      if (res.status === 200) {
        handleSelectedClubDetail(selectedClub);
        setDesignService({ ...designService, [key]: newValue });
        
      }

    } catch (error) {
      console.log("geo targeting", error);
    }
  };

const handleDesignServiceStatus = async () => {
  if (savingField) return;

  const newStatus = !designService.DesignServiceStatus;

  try {
    setSavingField("designServiceStatus");

    const payload = {
      designServiceStatus: newStatus,
      clubHandleDesign: newStatus,
      drsDesignSupport: newStatus,
    };

    const res = await callApi({
      method: "put",
      url: `club/update/${selectedClub.id}`,
      data: { fields: payload },
    });

    if (res.status === 200) {
      setDesignService((prev) => ({
        ...prev,
        DesignServiceStatus: newStatus,
      }));
      setClubHandleDesign(newStatus);
      setDrsDesignSupport(newStatus);

      handleSelectedClubDetail({
        ...selectedClub,
        ...payload,
      });

      toast.success(
        newStatus
          ? "Design Service enabled"
          : "Design Service disabled"
      );
    }
  } catch (err) {
    console.error("Design service toggle error", err);
  } finally {
    setSavingField(null);
  }
};


  const handleClubHandleDesignStatus = async () => {
    if (savingField) return;

    const newStatus = !clubHandleDesign;

    try {
      setSavingField("handleClubDesignStatus");

      const payload = {
        clubHandleDesign: newStatus,
        designServiceStatus: newStatus,
        drsDesignSupport: newStatus
      };

      const res = await callApi({
        method: "put",
        url: `club/update/${selectedClub.id}`,
        data: { fields: payload },
      });

      if (res.status === 200) {
        setClubHandleDesign(newStatus);
         setDesignService((prev) => ({
        ...prev,
        DesignServiceStatus: newStatus,
      }));
      setDrsDesignSupport(newStatus);
        toast.success("Club handle Design updated successfully",{id:"handleDesignToast"});
        handleSelectedClubDetail(selectedClub);
      }
    } catch (err) {
      console.error("Design service toggle error", err);
    } finally {
      setSavingField(null);
    }
  };

  const handleDrsDesignSupport = async () => {
    if (savingField) return;

    const newStatus = !drsDesignSupport;

    try {
      setSavingField("handleClubDesignStatus");

      const payload = {
        drsDesignSupport: newStatus,
        designServiceStatus: newStatus,
        clubHandleDesign: newStatus,
      };

      const res = await callApi({
        method: "put",
        url: `club/update/${selectedClub.id}`,
        data: { fields: payload },
      });

      if (res.status === 200) {
        setDrsDesignSupport(newStatus);
         setDesignService((prev) => ({
        ...prev,
        DesignServiceStatus: newStatus,
      }));
      setClubHandleDesign(newStatus);
        toast.success("DRS design support status updated successfully",{id:"handleDesignToast"});
        handleSelectedClubDetail(selectedClub);
      }
    } catch (err) {
      console.error("Design service toggle error", err);
    } finally {
      setSavingField(null);
    }
  };

  const handle15SecTimeDuration = async (data) => {
    setIsLoading(true);
    try {
      const pricingPayload = {
        clubId: selectedClub.id,
        durationType: "sec",
        duration: 15,
        price1Mon: Number(data.price1Month),
        price6Mon: Number(data.price6Month),
        price12Mon: Number(data.price12Month),
      };
      const res = await callApi({
        method: "patch",
        url: `club-pricing/${selectedClub.id}`,
        data: pricingPayload,
      });
      if (res.status === 200) {
        handleSelectedClubDetail(selectedClub);
        setOpenTimeDutaionModal(false);
      }
    } catch (error) {
      console.error("Error saving 15sec duration:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handle30SecTimeDuration = async (data) => {
    setIsLoading(true);
    try {
      const pricingPayload = {
        durationType: "sec",
        duration: 30,
        price1Mon: Number(data.price1Month),
        price6Mon: Number(data.price6Month),
        price12Mon: Number(data.price12Month),
        daypartStatus: data.dayPartStatus,
        ...(data.dayPartStatus && {
          dp10AmPercentage: data.daypartOpen10AM,
          dp10AmLimit: data.dp10AmLimit,
          dp10_4Percentage: data.daypart10AMto4PM,
          dp10_4Limit: data.dp10_4Limit,
          dp4PmPercentage: data.daypart4PMClose,
          dp4PmLimit: data.dp4PmLimit,
          dpWeekendPercentage: data.daypartWeekendsOnly,
          dpWeekendAmLimit: data.dpWeekendAmLimit,
        }),
      };

      const res = await callApi({
        method: "patch",
        url: `club-pricing/${selectedClub.id}`,
        data: pricingPayload,
      });
      if (res.status === 200) {
        handleSelectedClubDetail(selectedClub);
      }
    } catch (error) {
      console.error("Error saving 30sec duration:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const savePromotionalPricing = async (type, changes) => {
    const current = promoPricing[type];
    const updated = { ...current, ...changes };

    try {
      setSavingField(type);
      const pricingPayload = {
        type,
        status: updated.status,
        price1Mon: Number(updated.price1Mon || 0),
        price6Mon: Number(updated.price6Mon || 0),
        price12Mon: Number(updated.price12Mon || 0),
        spots: Number(updated.spots || 0),
      };

      const res = await callApi({
        method: "patch",
        url: `club-pricing/${selectedClub.id}`,
        data: pricingPayload,
      });
      if (res.status === 200) {
        setPromoPricing((prev) => ({ ...prev, [type]: updated }));
        handleSelectedClubDetail(selectedClub);
      }
    } catch (error) {
      console.error(`Error saving ${type} pricing:`, error);
    } finally {
      setSavingField(null);
    }
  };

  const handlePromoToggle = async (type) => {
    if (savingField) return;
    const newStatus = !promoPricing[type].status;
    try {
      setSavingField(type);
      const res = await callApi({
        method: "patch",
        url: `club-pricing/${selectedClub.id}`,
        data: { type, status: newStatus },
      });
      if (res.status === 200) {
        setPromoPricing((prev) => ({
          ...prev,
          [type]: { ...prev[type], status: newStatus },
        }));
        handleSelectedClubDetail(selectedClub);
      }
    } catch (error) {
      console.error(`Error toggling ${type}:`, error);
    } finally {
      setSavingField(null);
    }
  };

  const handlePromoFieldEdit = (type, key, newValue) =>
    savePromotionalPricing(type, {
      [key]: Number(String(newValue).replace(/[^0-9.]/g, "")),
    });

  const pricing30 = selectedClub?.pricings?.find(
    (p) => p.duration === 30 && !isPromoPricing(p)
  );
  const pricing15 = selectedClub?.pricings?.find(
    (p) => p.duration === 15 && !isPromoPricing(p)
  );


  const DayPartStatus = pricing30?.daypartStatus;

  const displayDayparts = DayPartStatus
    ? [
        {
          label: "Open",
          value: " Open 10:00 AM",
          discount: pricing30.dp10AmPercentage,
          adsLimit: pricing30.dp10AmLimit,
        },
        {
          label: "Time",
          value: "10:00 AM - 04:00 PM",
          discount: pricing30.dp10_4Percentage,
          adsLimit: pricing30.dp10_4Limit,
        },
        {
          label: "Close",
          value: "04:00 PM",
          discount: pricing30.dp4PmPercentage,
          adsLimit: pricing30.dp4PmLimit,
        },
        {
          label: "Weekends Only",
          value: "Weekends Only",
          discount: pricing30.dpWeekendPercentage,
          adsLimit: pricing30.dpWeekendAmLimit,
        },
      ]
    : [];

    const isCpmHideDisabled = savingField === "isCpmHide" || userRole === UserRole.Viewer
    const isDisabledGeoTarget =savingField === "geoToggle" || userRole === UserRole.Viewer;
    const isPromoDisabled = (type) => savingField === type || userRole === UserRole.Viewer;
    const isDisabledDesignService = savingField === "designServiceStatus" || userRole === UserRole.Viewer
  const isDisabledClubDesign =  savingField === "handleClubDesignStatus"  || userRole === UserRole.Viewer
    const isDisabledDesignSupport = savingField === "drsDesignSupport" || userRole === UserRole.Viewer

    console.log("savingField", savingField)
  return (
    <div className="space-y-6">
      {/*  15sec Duration */}
      {savingField === "isCpmHide" && <CommonLoader fullScreen/>}
      <div className="space-y-4">
        <div className="bg-white rounded-xl p-6 main-shadow">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-black tracking-[0] leading-[normal] mb-2">
                15sec Duration
              </h2>
              <p className="text-base font-normal text-textcolor tracking-[0] leading-[20px]">
                Set pricing for short-format ad spots across different
                subscription plans.
              </p>
            </div>
            <button
              onClick={() => setOpenTimeDutaionModal(true)}
                disabled={userRole === UserRole.Viewer}
              className={` ${userRole === UserRole.Viewer ? "cursor-not-allowed opacity-50" : "cursor-pointer"} flex items-center gap-1 text-base font-medium text-black hover:text-primary transition-colors`}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.3879 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L4.99967 13.6667L1.33301 14.6667L2.33301 11L11.333 2.00004Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Edit
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <EditableInfoCard
              className="!bg-[#F4F3F3] geo-target"
              title="1 Month"
              value={formatCurrency(pricing15?.price1Mon)}
              validationType="currency"
              fieldName="One-time Setup Fee"
              showEditButton={false}
            />
            <EditableInfoCard
              className="!bg-[#F4F3F3] geo-target"
              title="6 Month"
              value={formatCurrency(pricing15?.price6Mon)}
              validationType="currency"
              fieldName="Monthly Recurring Fee"
              showEditButton={false}
            />
            <EditableInfoCard
              className="!bg-[#F4F3F3] geo-target"
              title="12 Month"
              value={formatCurrency(pricing15?.price12Mon)}
              validationType="currency"
              fieldName="Monthly Recurring Fee"
              showEditButton={false}
            />
          </div>
        </div>
      </div>

      {/*  30sec Duration */}
      <div className="space-y-4">
        <div className="bg-white rounded-xl p-6 main-shadow">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-black tracking-[0] leading-[normal] mb-2">
                30 sec Duration
              </h2>
              <p className="text-base font-normal text-textcolor tracking-[0] leading-[20px]">
                Define pricing for extended ad playback across multiple plans.
              </p>
            </div>
            <button
              onClick={() => setOpen30SecModal(true)}
              disabled={userRole === UserRole.Viewer}
              className={` ${userRole === UserRole.Viewer ? "cursor-not-allowed opacity-50" : "cursor-pointer"} flex items-center gap-1 text-base font-medium text-black hover:text-primary transition-colors`}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.3879 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L4.99967 13.6667L1.33301 14.6667L2.33301 11L11.333 2.00004Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Edit
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <EditableInfoCard
              className="!bg-[#F4F3F3] geo-target"
              title="1 Month"
              value={formatCurrency(pricing30?.price1Mon)}
              validationType="currency"
              fieldName="One-time Setup Fee"
              showEditButton={false}
            />
            <EditableInfoCard
              className="!bg-[#F4F3F3] geo-target"
              title="6 Month"
              value={formatCurrency(pricing30?.price6Mon)}
              validationType="currency"
              fieldName="Monthly Recurring Fee"
              showEditButton={false}
            />
            <EditableInfoCard
              className="!bg-[#F4F3F3] geo-target"
              title="12 Month"
              value={formatCurrency(pricing30?.price12Mon)}
              validationType="currency"
              fieldName="Monthly Recurring Fee"
              showEditButton={false}
            />
          </div>

          {/* Choose your daypart */}
          <div className="flex items-center justify-between mb-4 mt-5">
            <div>
              <h2 className="text-xl font-semibold text-black tracking-[0] leading-[normal] mb-2">
                Daypart
              </h2>
              <p className="text-base font-normal text-textcolor tracking-[0] leading-[20px]">
                Adjust pricing based on time-of-day and high-traffic viewing
                windows.
              </p>
            </div>
            <button
              // onClick={() => setIsDaypartEnabled(!isDaypartEnabled)}
              className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none cursor-not-allowed opacity-55"
              style={{
                backgroundColor: DayPartStatus ? "#4CAF50" : "#e5e7eb",
              }}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  DayPartStatus ? "translate-x-5" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {DayPartStatus && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {displayDayparts.map((daypart, index) => (
                <div key={index} className="space-y-4">
                  {/* Time and Discount Row */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Time/Label Card */}
                    <div className="bg-[#F4F3F3] rounded-lg p-4">
                      <p className="text-sm font-normal text-textcolor tracking-[0] leading-[normal] mb-2">
                        {daypart.value}
                      </p>
                      <h3 className="text-[32px] font-semibold text-black tracking-[0] leading-[normal]">
                        {daypart.discount}%
                      </h3>
                    </div>

                    {/* Discount Card */}
                    <div className="bg-[#F4F3F3] rounded-lg p-4">
                      <p className="text-sm font-normal text-textcolor tracking-[0] leading-[normal] mb-2">
                        Ads Limit
                      </p>
                      <h3 className="text-[32px] font-semibold text-black tracking-[0] leading-[normal]">
                        {daypart.adsLimit}
                      </h3>
                    </div>
                  </div>

                  {/* Ads Limit Card */}
                  {/* <div className="bg-[#F4F3F3] rounded-lg p-4">
                  <p className="text-sm font-normal text-textcolor tracking-[0] leading-[normal] mb-2">
                    Ads Limit
                  </p>
                  <h3 className="text-[32px] font-semibold text-black tracking-[0] leading-[normal]">
                    
                  </h3>
                </div> */}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/*  Weather / Sports / Finance */}
      {PROMOTIONAL_SECTIONS.map(({ type, title, description }) => {
        const promo = promoPricing[type];
        const isEnabled = promo.status;
        const isDisabled = isPromoDisabled(type);
        const promoFields = [
          { key: "price1Mon", title: "1 Month", isCurrency: true },
          { key: "price6Mon", title: "6 Month", isCurrency: true },
          { key: "price12Mon", title: "12 Month", isCurrency: true },
          { key: "spots", title: "Spots", isCurrency: false },
        ];

        return (
          <div key={type} className="space-y-4">
            <div className="bg-white rounded-xl p-6 main-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="pr-4">
                  <h2 className="text-xl font-semibold text-black tracking-[0] leading-[normal] mb-2">
                    {title}
                  </h2>
                  <p className="text-base font-normal text-textcolor tracking-[0] leading-[20px]">
                    {description}
                  </p>
                </div>

                {/* Toggle */}
                <button
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                  onClick={() => handlePromoToggle(type)}
                  disabled={isDisabled}
                  style={{
                    backgroundColor: isEnabled ? "#4CAF50" : "#e5e7eb",
                  }}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      isEnabled ? "translate-x-5" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              {isEnabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
                  {promoFields.map(({ key, title: fieldTitle, isCurrency }) => (
                    <EditableInfoCard
                      key={key}
                      className="!bg-[#F4F3F3] geo-target"
                      title={fieldTitle}
                      value={
                        isCurrency
                          ? formatCurrency(promo[key])
                          : String(promo[key] || 0)
                      }
                      onSave={(newValue) =>
                        handlePromoFieldEdit(type, key, newValue)
                      }
                      isLoading={savingField === type}
                      validationType={isCurrency ? "currency" : "numeric"}
                      fieldName={`${title} ${fieldTitle}`}
                      showEditButton={userRole !== UserRole.Viewer}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Hide Cpm Option */}
        <div className="space-y-4">
        <div className="bg-white rounded-xl p-6 main-shadow">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            {/* Title + Description */}
            <div className="pr-4">
              <h2 className="text-xl font-semibold text-black mb-2">
               Average CPM
              </h2>
              <p className="text-base text-textcolor leading-[20px]">
               Show CPM on Website
              </p>
            </div>

            {/* Toggle */}
            <button
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors  flex-shrink-0  ${isCpmHideDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
              onClick={HideCpmOption}
              disabled={isCpmHideDisabled}
              style={{
                backgroundColor: !isCpmVisible
                  ? "#4CAF50"
                  : "#e5e7eb",
              }}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white
            transition-transform ${
              !isCpmVisible ? "translate-x-5" : "translate-x-1"
            }`}
              />
            </button>
          </div>
        </div>
      </div>
      {/* Geo Targeting */}
      <div className="space-y-4">
        <div className="bg-white rounded-xl p-6 main-shadow">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            {/* Title + Description */}
            <div className="pr-4">
              <h2 className="text-xl font-semibold text-black mb-2">
                Geo Targeting
              </h2>
              <p className="text-base text-textcolor leading-[20px]">
                Allow advertisers to target specific geographic areas within the
                venue for more precise audience targeting.
              </p>
            </div>

            {/* Toggle */}
            <button
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors  flex-shrink-0  ${isDisabledGeoTarget ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
              onClick={handleGeoToggle}
              disabled={isDisabledGeoTarget}
              style={{
                backgroundColor: geoTargeting.geoTargetStatus
                  ? "#4CAF50"
                  : "#e5e7eb",
              }}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white
            transition-transform ${
              geoTargeting.geoTargetStatus ? "translate-x-5" : "translate-x-1"
            }`}
              />
            </button>
          </div>

          {/* Inputs */}
          {geoTargeting.geoTargetStatus && (
            <div className="">
              <div>
                {/* <label className="block text-sm font-medium text-black mb-2">
                  One-time Setup Fee
                </label> */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <EditableInfoCard
                    className="!bg-[#F4F3F3] geo-target"
                    title=" One-time Setup Fee"
                    value={formatCurrency(geoTargeting.setupFee)}
                    onSave={(newValue) =>
                      handleGeoTargetingEdit("setupFee", newValue)
                    }
                    isLoading={savingField === "setupFee"}
                    validationType="currency-non-zero"
                    fieldName="One-time Setup Fee"
                  />
                  <EditableInfoCard
                    className="!bg-[#F4F3F3] geo-target"
                    title="Monthly Recurring Fee"
                    value={formatCurrency(geoTargeting.monthlyFee)}
                    onSave={(newValue) =>
                      handleGeoTargetingEdit("monthlyFee", newValue)
                    }
                    isLoading={savingField === "monthlyFee"}
                    validationType="currency-non-zero"
                    fieldName="Monthly Recurring Fee"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Design Service */}
      <div className="bg-white rounded-xl p-6 main-shadow">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-black tracking-[0] leading-[normal] mb-2">
              Design Service
            </h2>
            <p className="text-base font-normal text-textcolor tracking-[0] leading-[20px]">
              Professional design service to help advertiser create compelling
              ad content optimized for digital screens.
            </p>
          </div>
          {/* Toggle */}
          <button
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors  flex-shrink-0  ${isDisabledDesignService ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
            onClick={handleDesignServiceStatus}
            disabled={isDisabledDesignService}
            style={{
              backgroundColor: designService.DesignServiceStatus
                ? "#4CAF50"
                : "#e5e7eb",
            }}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white
            transition-transform ${
              designService.DesignServiceStatus
                ? "translate-x-5"
                : "translate-x-1"
            }`}
            />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {designService.DesignServiceStatus && (
            <div>
              <EditableInfoCard
                className="!bg-[#F4F3F3] geo-target"
                title="One-time setup Fee"
                value={formatCurrency(designService.designFee)}
                onSave={(newValue) =>
                  handleDesignServiceEdit("designFee", newValue)
                }
                isLoading={savingField === "designFee"}
                // validationType={"currency-non-zero"}
                fieldName="One-time setup Fee"
              />
            </div>
          )}
        </div>
      </div>

      {/* Club Handle Design */}
      <div className="bg-white rounded-xl p-6 main-shadow">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-black tracking-[0] leading-[normal] mb-2">
              Club Handle Design
            </h2>
            <p className="text-base font-normal text-textcolor tracking-[0] leading-[20px]">
              The club is responsible for delivering finished ad files for all
              campaigns.,
            </p>
          </div>
          <button
            onClick={handleClubHandleDesignStatus}
            disabled={isDisabledClubDesign}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${isDisabledClubDesign ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
            style={{
              backgroundColor: clubHandleDesign ? "#4CAF50" : "#e5e7eb",
            }}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                clubHandleDesign ? "translate-x-5" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        <div className="flex items-center justify-between mb-0">
          <div>
            <h2 className="text-xl font-semibold text-black tracking-[0] leading-[normal] mb-2">
              DRS Design Support
            </h2>
            <p className="text-base font-normal text-textcolor tracking-[0] leading-[20px]">
              DRS will produce professional ad creative on behalf of
              advertisers. Additional design fees may apply.
            </p>
          </div>
          <button
            onClick={handleDrsDesignSupport}
            disabled={isDisabledDesignSupport}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${isDisabledClubDesign ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
            style={{
              backgroundColor: drsDesignSupport ? "#4CAF50" : "#e5e7eb",
            }}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                drsDesignSupport ? "translate-x-5" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      {openTimeDutaionModal && (
        <TimeDurationModal
          title="15sec Duration"
          description="Set pricing for short-format ad spots across different subscription plans."
          onClose={() => setOpenTimeDutaionModal(false)}
          onSave={handle15SecTimeDuration}
          isLoading={isLoading}
          durationType={15}
          initialData={{
            price1Month: pricing15?.price1Mon,
            price6Month: pricing15?.price6Mon,
            price12Month: pricing15?.price12Mon,
          }}
        />
      )}

      {open30SecModal && (
        <TimeDurationModal
          title="30sec Duration"
          description="Define pricing for extended ad playback across multiple plans."
          onClose={() => setOpen30SecModal(false)}
          onSave={handle30SecTimeDuration}
          isLoading={isLoading}
          durationType={30}
          initialData={{
            price1Month: pricing30?.price1Mon,
            price6Month: pricing30?.price6Mon,
            price12Month: pricing30?.price12Mon,
            dayPartStatus: pricing30?.daypartStatus,
            daypartOpen10AM: pricing30?.dp10AmPercentage,
            dp10AmLimit: pricing30?.dp10AmLimit,
            daypart10AMto4PM: pricing30?.dp10_4Percentage,
            dp10_4Limit: pricing30?.dp10_4Limit,
            daypart4PMClose: pricing30?.dp4PmPercentage,
            dp4PmLimit: pricing30?.dp4PmLimit,
            daypartWeekendsOnly: pricing30?.dpWeekendPercentage,
            dpWeekendAmLimit: pricing30?.dpWeekendAmLimit,
          }}
        />
      )}
    </div>
  );
};

export default PricingTab;
